/**
 * Bitacora de fallos de la pasarela.
 *
 * POR QUE EXISTE
 * --------------
 * Del 10 al 12 de agosto de 2026 el historial de ventas estuvo EN BLANCO mientras
 * la caja registraba perfecto: folio, stock, kardex y corte cuadrando en la base,
 * y la pantalla diciendo "Sin ventas registradas". La causa era un `300 PGRST201`
 * por una incrustacion ambigua, y estuvo tres dias ahi porque **nadie vio ese
 * 300**. El dueño no vio un error: vio que el dinero del dia no existia.
 *
 * `lib/api.ts` es el unico punto por donde pasan todas las peticiones, o sea el
 * unico lugar donde esto se puede apuntar una sola vez y valer para las veinte
 * pantallas.
 *
 * QUE SE GUARDA, Y SOBRE TODO QUE NO
 * ----------------------------------
 * SI: la ruta, el metodo, el codigo de error, el estado HTTP y la hora. Con eso
 * se identifica el fallo sin ambiguedad — un `PGRST201` en
 * `/api/stores/1/sales` dice exactamente que se rompio.
 *
 * NO: el CUERPO de la peticion, jamas. Por aqui pasan `/api/auth/login` con la
 * contraseña del cajero, altas de cliente con nombre y telefono, y ventas con su
 * ticket. Una bitacora de diagnostico que guarda contraseñas es un problema mas
 * grande que el que resuelve.
 *
 * NO: los VALORES de la consulta, solo sus nombres. `search` puede llevar el
 * nombre de un cliente. Los nombres bastan para diagnosticar: lo que se necesita
 * saber es que la ruta se llamo con `onlyLowStock`, no que alguien buscaba a
 * "Rosa".
 *
 * POR QUE EN `localStorage` Y NO EN UN SERVICIO
 * --------------------------------------------
 * Porque mandar los fallos a un tercero es sacar datos del negocio a un servicio
 * ajeno, y eso lo decide el dueño, no este archivo. Esto vive en el aparato, con
 * tope de tamaño, y sirve para lo que hoy no se puede hacer de ninguna forma:
 * llegar a la tablet del mostrador y ver que fallo de verdad, en vez de pedirle
 * al cajero que describa una pantalla vacia.
 *
 * ESTO NUNCA PUEDE LANZAR. Va en el camino de cada peticion, incluida la venta.
 * `localStorage` lanza con la cuota llena y en algunos modos privados, asi que
 * todo va envuelto: si la bitacora falla, se pierde la bitacora, no la venta.
 */

const LLAVE = 'abarrotes.errlog';
const TOPE = 50;

export interface FalloApi {
  /** Milisegundos UTC del reloj del aparato. Para ordenar, no para auditar. */
  at: number;
  path: string;
  method: string;
  /** El codigo del dominio (`VALIDATION_ERROR`, `FORBIDDEN`, ...) si lo hubo. */
  code: string;
  status: number;
  /** El codigo de PostgREST (`PGRST201`, `42501`, ...) cuando viene. */
  pg?: string;
  /** Solo los NOMBRES de los parametros de consulta. Nunca sus valores. */
  queryKeys?: string[];
}

function leerCrudo(): FalloApi[] {
  try {
    const texto = localStorage.getItem(LLAVE);
    if (!texto) return [];
    const datos: unknown = JSON.parse(texto);
    return Array.isArray(datos) ? (datos as FalloApi[]) : [];
  } catch {
    return [];
  }
}

/**
 * Saca el codigo CRUDO de PostgREST o Postgres.
 *
 * Es el dato que de verdad identifica el fallo: `PGRST201` es la incrustacion
 * ambigua, `42501` es `permission denied`, `42703` es una columna que no existe.
 * Los tres se ven igual en pantalla —una lista vacia— y son cosas distintas.
 *
 * Viene en `pgCode`, que `ApiError` conserva justo para esto. Se mira tambien
 * `code` como respaldo, para los errores que llegan aqui sin pasar por
 * `toApiError` (un fallo de red, algo que lance supabase-js por su cuenta).
 */
function codigoPostgrest(err: unknown): string | undefined {
  if (typeof err !== 'object' || err === null) return undefined;
  const e = err as { pgCode?: unknown; code?: unknown };
  if (typeof e.pgCode === 'string' && e.pgCode !== '') return e.pgCode;
  if (typeof e.code === 'string' && e.code !== '') return e.code;
  return undefined;
}

/**
 * Lo que NO se apunta, porque no es un fallo.
 *
 * No tener sesion es una RESPUESTA VALIDA, no una averia: `session.tsx` la trata
 * asi desde siempre y la aplicacion reacciona mandando a la pantalla de acceso.
 * Apuntarla llenaba la bitacora de ruido y —peor— hacia que la pastilla naciera
 * en ROJO con "2 fallos" en la pantalla de acceso, antes de que nadie tocara
 * nada. Se vio al correrlo, no al escribirlo.
 *
 * Es la regla de la campanita del CLAUDE.md §10 aplicada al diagnostico: una
 * lista que avisa de lo que pasa siempre se deja de leer entera, y entonces el
 * aviso que si importaba tampoco se ve.
 *
 * La lista es CORTA a proposito. `VALIDATION_ERROR` **no** esta aqui aunque
 * tambien lo produce un dato mal tecleado: es el codigo con el que se manifesto
 * el defecto del escaneo —`lookup_scan` recibiendo cadena vacia— y filtrarlo
 * dejaria ciega a la bitacora justo para el caso que la hizo nacer.
 */
const NO_SON_FALLOS = new Set(['UNAUTHENTICATED', 'SESSION_EXPIRED']);

/** Apunta un fallo. No lanza nunca, pase lo que pase. */
export function recordApiFailure(
  path: string,
  method: string,
  err: unknown,
  queryKeys?: string[],
): void {
  try {
    const codigo = (err as { code?: unknown } | null)?.code;
    if (typeof codigo === 'string' && NO_SON_FALLOS.has(codigo)) return;
    const e = err as { code?: unknown; status?: unknown } | null;
    const entrada: FalloApi = {
      at: Date.now(),
      // La ruta lleva ids de sucursal y de producto, que son justo lo que hace
      // falta para reproducir. No llevan datos de personas.
      path: path.split('?')[0] ?? path,
      method,
      code: typeof e?.code === 'string' ? e.code : 'DESCONOCIDO',
      status: typeof e?.status === 'number' ? e.status : 0,
    };
    const pg = codigoPostgrest(err);
    if (pg !== undefined && pg !== entrada.code) entrada.pg = pg;
    if (queryKeys && queryKeys.length > 0) entrada.queryKeys = queryKeys;

    const previos = leerCrudo();
    previos.push(entrada);
    localStorage.setItem(LLAVE, JSON.stringify(previos.slice(-TOPE)));

    // Tambien a la consola, que es donde lo va a ver quien este depurando en
    // escritorio con las herramientas abiertas.
    console.error(
      `[api] ${entrada.method} ${entrada.path} -> ${entrada.code}` +
        (entrada.pg ? ` (${entrada.pg})` : '') +
        ` [${entrada.status}]`,
    );
  } catch {
    // Si la bitacora no se puede escribir, se pierde la bitacora. Nunca la venta.
  }
}

/** Los fallos apuntados, del mas viejo al mas reciente. */
export function readApiFailures(): FalloApi[] {
  return leerCrudo();
}

/** Vacia la bitacora. */
export function clearApiFailures(): void {
  try {
    localStorage.removeItem(LLAVE);
  } catch {
    // Da igual: si no se puede borrar, la bitacora se recicla sola por el tope.
  }
}
